import type { BackendOccasionType } from '@/src/types';

/**
 * Carries a catalog Product selection across the login redirect. This app
 * has no existing "return to originally requested page" mechanism for
 * `/login` (verified — `ProtectedRoute.tsx` and `Login.tsx` both hardcode
 * `/dashboard`), so this is a new, minimal, additive piece built for this
 * feature per architecture §2.4 — not a repurposed existing convention.
 */
const STORAGE_KEY = 'invitecloud.catalog_pending_selection.v1';

// sessionStorage is same-origin but still an untrusted boundary once JSON is
// parsed back out of it — validate against the real enum before handing it
// to the wizard's typed WizardData, rather than trusting the stored string.
const VALID_OCCASION_TYPES: readonly BackendOccasionType[] = [
  'WEDDING',
  'BIRTHDAY',
  'ANNIVERSARY',
  'SEND_OFF',
  'BABY_SHOWER',
  'ENGAGEMENT',
  'CUSTOM',
];
export function isBackendOccasionType(value: string): value is BackendOccasionType {
  return (VALID_OCCASION_TYPES as readonly string[]).includes(value);
}

// A selection older than this is assumed to belong to an abandoned catalog
// visit, not the sign-in the user is completing right now — without this, a
// user who clicks Select, walks away, and signs in later for an unrelated
// reason would be silently dropped into the wizard instead of /dashboard.
const MAX_AGE_MS = 15 * 60 * 1000;

export interface PendingCatalogSelection {
  productId?: string;
  templateId: string;
  occasionType: BackendOccasionType;
}

interface StoredSelection extends PendingCatalogSelection {
  storedAt: number;
}

export function setPendingCatalogSelection(selection: PendingCatalogSelection): void {
  try {
    const stored: StoredSelection = { ...selection, storedAt: Date.now() };
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // sessionStorage unavailable (private browsing, etc.) — selection is lost,
    // user lands on /dashboard after login same as any other bare sign-in.
  }
}

export function consumePendingCatalogSelection(): PendingCatalogSelection | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(STORAGE_KEY);
    const parsed = JSON.parse(raw) as Partial<StoredSelection>;
    if (typeof parsed.templateId !== 'string' || typeof parsed.occasionType !== 'string') {
      return null;
    }
    if (parsed.productId !== undefined && typeof parsed.productId !== 'string') {
      return null;
    }
    if (!isBackendOccasionType(parsed.occasionType)) {
      return null;
    }
    if (typeof parsed.storedAt !== 'number' || Date.now() - parsed.storedAt > MAX_AGE_MS) {
      return null;
    }
    return {
      ...(parsed.productId ? { productId: parsed.productId } : {}),
      templateId: parsed.templateId,
      occasionType: parsed.occasionType,
    };
  } catch {
    return null;
  }
}
